import {
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSPanel,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import { queriesPayers } from "@query";
import { InfoItem } from "../../components/ClientInfoPanel/InfoItem";
import { copyText } from "../../../../utils/copyText";

import styles from "./styles.module.scss";

export interface IWireTransferSectionProps extends WSElementProps {
  payerId: string;
}

/**
 * The processing account this client wires to. It carries full bank credentials,
 * so v3 keeps it off the ordinary payer read and behind its own audited,
 * no-store endpoint — hence a dedicated query rather than a field on the client.
 * A client with no processing account set up 404s, so the section is absent
 * rather than empty.
 */
export const WireTransferSection: React.FC<IWireTransferSectionProps> = ({
  payerId,
  ...elementProps
}) => {
  const queryProcessingAccount =
    queriesPayers.getPayerBankTransferProcessingAccount.useQuery(
      { payerId },
      { enabled: !!payerId, retry: false }
    );

  const processingAccount = queryProcessingAccount.data;

  if (!processingAccount?.routingNumber) {
    return null;
  }

  const rows: Array<{ label: string; value: string; copiedMessage: string }> = [
    {
      label: "Bank name",
      value: processingAccount.bankName,
      copiedMessage: "Bank name copied to clipboard"
    },
    {
      label: "Routing number",
      value: processingAccount.routingNumber,
      copiedMessage: "Routing number copied to clipboard"
    },
    {
      label: "Account number",
      value: processingAccount.accountNumber,
      copiedMessage: "Account number copied to clipboard"
    }
  ];

  return (
    <WSElement {...elementProps}>
      <WSSectionToolbar
        title="Wire transfers"
        tooltip={{
          title: "Wire transfers",
          text: "This is the secure payment gateway setup for this client that routes money to your direct deposit without you having to provide your own banking info."
        }}
      />

      <WSPanel className={styles.panel}>
        {rows.map(row => (
          <WSFlexBox.CenterY key={row.label} mb="XL" justify="space-between">
            <InfoItem label={row.label} value={row.value} />
            <WSButton.Link
              onClick={() => {
                copyText(row.value, row.copiedMessage);
              }}
            >
              Copy
            </WSButton.Link>
          </WSFlexBox.CenterY>
        ))}
      </WSPanel>
    </WSElement>
  );
};
